/**
 * Replay tab.
 *
 * Loads an EventLog export JSON file (the shape EventLogTab.tsx
 * "Download JSON" produces, or a bare entries array). The timeline is the
 * filtered list of CP-initiated CALLs sorted ascending. The user can:
 *   - Skip individual rows or all rows of a given action (e.g. Heartbeat).
 *   - Pick a replay speed: 1x (200 ms pause between steps) or Instant.
 *   - Play → walks the timeline, fires each non-skipped row through the
 *     live OcppV16Adapter via useStationStore.createUploadPort-style
 *     adapter access. Stops on the first CALLERROR.
 *   - Stop → cooperative cancel of the run loop.
 *   - Reset → clears every step's status without losing the timeline.
 *
 * Per-step JSON editing, Range From/To filtering, and ".log file" parsing
 * are intentionally deferred — the EventLog JSON shape already gives QA
 * the round-trip "capture in evse3, replay in evse3" workflow.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { ConnectionState } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useReplayStore, type ReplaySender, type ReplaySpeed } from '@/state/useReplayStore';
import type { ReplayStep } from '@/state/replayTimeline';
import { FileDropZone } from '@/ui/components/FileDropZone';

export function ReplayTab(): JSX.Element {
  const fixtureRef = useRef<HTMLTextAreaElement | null>(null);

  const steps = useReplayStore((s) => s.steps);
  const status = useReplayStore((s) => s.status);
  const currentIndex = useReplayStore((s) => s.currentIndex);
  const loadError = useReplayStore((s) => s.loadError);
  const selectedSeq = useReplayStore((s) => s.selectedSeq);
  const load = useReplayStore((s) => s.load);
  const toggleSkip = useReplayStore((s) => s.toggleSkip);
  const skipAllOfAction = useReplayStore((s) => s.skipAllOfAction);
  const setSkippedAll = useReplayStore((s) => s.setSkippedAll);
  const setSkippedAbove = useReplayStore((s) => s.setSkippedAbove);
  const setSkippedBelow = useReplayStore((s) => s.setSkippedBelow);
  const selectSeq = useReplayStore((s) => s.selectSeq);
  const setStepPayload = useReplayStore((s) => s.setStepPayload);
  const revertStepPayload = useReplayStore((s) => s.revertStepPayload);
  const reset = useReplayStore((s) => s.reset);
  const run = useReplayStore((s) => s.run);
  const pause = useReplayStore((s) => s.pause);
  const resume = useReplayStore((s) => s.resume);
  const stop = useReplayStore((s) => s.stop);

  const connectionState = useStationStore((s) => s.connectionState);
  const createUploadPort = useStationStore((s) => s.createUploadPort);

  const [speed, setSpeed] = useState<ReplaySpeed>('instant');
  const [runError, setRunError] = useState<string | null>(null);
  // FEAT-025 — expanded rows show the inline detail/edit panel.
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());

  const toggleExpand = (seq: number): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });
  };

  const loadFile = (file: File): void => {
    void file
      .text()
      .then((text) => load(JSON.parse(text)))
      .catch((err: unknown) => {
        setRunError(err instanceof Error ? err.message : String(err));
      });
  };

  const handleFixtureLoad = (): void => {
    const text = fixtureRef.current?.value ?? '';
    if (text.trim().length === 0) return;
    try {
      load(JSON.parse(text));
      setRunError(null);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : String(err));
    }
  };

  const handlePlay = (): void => {
    setRunError(null);
    const port = createUploadPort();
    if (!port) {
      setRunError('Connect the station first — no live adapter to replay through.');
      return;
    }
    // We don't use port.upload (which is StartTransaction-shaped); the
    // OfflineUploadPort interface assumes a Start/MV/Stop triplet. For
    // arbitrary CALLs we close over the adapter via useStationStore's
    // internal getter — exposed for tests through the same WeakMap entry.
    const sender: ReplaySender = async (action, payload) => {
      const result = await sendThroughAdapter(action, payload);
      return result;
    };
    void run(sender, speed);
  };

  const canPlay =
    connectionState === ConnectionState.Connected &&
    steps.length > 0 &&
    status !== 'running' &&
    status !== 'paused' &&
    status !== 'done';

  const counts = countStatuses(steps);
  // FEAT-009 — "N of M to play" indicator.
  const toPlay = steps.filter((s) => !s.skipped).length;

  return (
    <div
      role="tabpanel"
      id="tab-panel-replay"
      aria-labelledby="tab-replay"
      className="space-y-4 p-4"
      data-testid="replay-tab"
    >
      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-1 text-sm font-medium text-slate-100">Load timeline</h2>
        <p className="mb-2 text-[11px] text-slate-500">
          Use the Event Log&apos;s <span className="text-slate-300">Download JSON</span> export, or a{' '}
          <span className="font-mono text-slate-300">{'{ "raw": [ { "action", "payload" }, … ] }'}</span>{' '}
          object. Only outbound CP-initiated CALLs (StartTransaction, MeterValues, StopTransaction, …)
          become replayable steps — Heartbeat / StatusNotification and CSMS-initiated frames are ignored.
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <FileDropZone
            accept="application/json,.json"
            onFile={loadFile}
            prompt="Drop an Event Log JSON export here, or"
            inputTestId="replay-file"
          />
          <span className="text-slate-500">or paste JSON:</span>
          <textarea
            ref={fixtureRef}
            placeholder={'{"raw":[{"action":"StartTransaction","payload":{…}}]}'}
            className="h-10 w-64 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-[10px] text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="replay-fixture-input"
          />
          <button
            type="button"
            onClick={handleFixtureLoad}
            className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
            data-testid="replay-fixture-load"
          >
            Load
          </button>
          {loadError ? (
            <span
              className="rounded bg-rose-900/40 px-2 py-1 text-rose-200"
              data-testid="replay-load-error"
            >
              {loadError}
            </span>
          ) : null}
        </div>
      </section>

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <header className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-slate-100">Timeline</h2>
          <span className="text-[10px] text-slate-500" data-testid="replay-counts">
            {steps.length} steps · {counts.done} done · {counts.skipped} skipped · {counts.failed}{' '}
            failed · {counts.pending} pending
          </span>
        </header>
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
          {/* FEAT-009 — bulk skip controls. */}
          <button
            type="button"
            onClick={() => setSkippedAll(true)}
            disabled={steps.length === 0 || status === 'running' || status === 'paused'}
            className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-skip-all"
          >
            Skip all
          </button>
          <button
            type="button"
            onClick={() => setSkippedAll(false)}
            disabled={steps.length === 0 || status === 'running' || status === 'paused'}
            className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-unskip-all"
          >
            Unskip all
          </button>
          <button
            type="button"
            onClick={() => selectedSeq !== null && setSkippedAbove(selectedSeq)}
            disabled={selectedSeq === null || status === 'running' || status === 'paused'}
            title={
              selectedSeq === null
                ? 'Select a row first'
                : `Skip rows above #${selectedSeq}`
            }
            className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-skip-above"
          >
            Skip above
          </button>
          <button
            type="button"
            onClick={() => selectedSeq !== null && setSkippedBelow(selectedSeq)}
            disabled={selectedSeq === null || status === 'running' || status === 'paused'}
            title={
              selectedSeq === null
                ? 'Select a row first'
                : `Skip rows below #${selectedSeq}`
            }
            className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-skip-below"
          >
            Skip below
          </button>
          <button
            type="button"
            onClick={() => skipAllOfAction('Heartbeat')}
            disabled={steps.length === 0 || status === 'running' || status === 'paused'}
            className="rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-skip-heartbeats"
          >
            Skip Heartbeats
          </button>
          <label className="flex items-center gap-1 text-slate-400">
            Speed
            <select
              value={speed}
              onChange={(e) => setSpeed(e.target.value as ReplaySpeed)}
              className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
              data-testid="replay-speed"
            >
              <option value="instant">Instant</option>
              <option value="10x">10x (50 ms gap)</option>
              <option value="1x">1x (200 ms gap)</option>
              <option value="realtime">Real-time</option>
            </select>
          </label>
          <span className="text-slate-400" data-testid="replay-to-play">
            {toPlay} of {steps.length} to play
          </span>
          <button
            type="button"
            disabled={!canPlay}
            onClick={handlePlay}
            className="rounded bg-primary-base px-3 py-1 font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-play"
          >
            {status === 'running' ? 'Running…' : 'Play'}
          </button>
          {/* NIT-015 — Pause / Resume button next to Play. */}
          {status === 'paused' ? (
            <button
              type="button"
              onClick={resume}
              className="rounded bg-amber-700 px-3 py-1 text-amber-50 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="replay-resume"
            >
              Resume
            </button>
          ) : (
            <button
              type="button"
              disabled={status !== 'running'}
              onClick={pause}
              className="rounded bg-amber-700 px-3 py-1 text-amber-50 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="replay-pause"
            >
              Pause
            </button>
          )}
          <button
            type="button"
            disabled={status !== 'running' && status !== 'paused'}
            onClick={stop}
            className="rounded bg-rose-700 px-3 py-1 text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-stop"
          >
            Stop
          </button>
          <button
            type="button"
            disabled={steps.length === 0 || status === 'running' || status === 'paused'}
            onClick={reset}
            className="rounded bg-slate-700 px-3 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="replay-reset"
          >
            Reset
          </button>
          <span className="ml-auto text-[10px] text-slate-500" data-testid="replay-status">
            status: {status}
          </span>
          {runError ? (
            <span
              className="rounded bg-rose-900/40 px-2 py-1 text-rose-200"
              data-testid="replay-run-error"
            >
              {runError}
            </span>
          ) : null}
        </div>
        {steps.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="replay-empty">
            No timeline loaded.
          </p>
        ) : (
          <table className="w-full text-left text-xs" data-testid="replay-table">
            <thead className="text-slate-500">
              <tr>
                <th className="w-6 px-1 py-1"></th>
                <th className="px-2 py-1">#</th>
                <th className="px-2 py-1">action</th>
                <th className="px-2 py-1">status</th>
                <th className="px-2 py-1">skip</th>
                <th className="px-2 py-1">error</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((s, i) => {
                const cls =
                  s.status === 'failed'
                    ? 'text-rose-300'
                    : s.status === 'done'
                      ? 'text-primary-soft'
                      : s.status === 'running' || i === currentIndex
                        ? 'text-amber-300'
                        : s.status === 'skipped'
                          ? 'text-slate-500'
                          : 'text-slate-300';
                const isSelected = selectedSeq === s.seq;
                const isExpanded = expanded.has(s.seq);
                // FEAT-025 — payload may have been edited via the inline panel.
                const modified =
                  JSON.stringify(s.payload) !== JSON.stringify(s.originalPayload);
                return (
                  <Fragment key={s.seq}>
                    <tr
                      onClick={() => selectSeq(isSelected ? null : s.seq)}
                      className={`cursor-pointer border-t border-slate-800 ${
                        isSelected ? 'bg-primary-base/20' : 'hover:bg-slate-800/60'
                      }`}
                      data-testid={`replay-row-${s.seq}`}
                      aria-selected={isSelected}
                    >
                      <td
                        className="px-1 py-1 text-center text-slate-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(s.seq);
                        }}
                      >
                        <button
                          type="button"
                          className="rounded px-1 text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Collapse detail' : 'Expand detail'}
                          data-testid={`replay-expand-${s.seq}`}
                          title={isExpanded ? 'Hide payload' : 'Show / edit payload'}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                      </td>
                      <td className="px-2 py-1 font-mono text-slate-100">
                        {s.seq}
                        {modified ? (
                          <span
                            className="ml-1 inline-block h-2 w-2 rounded-full bg-amber-400 align-middle"
                            title="Payload modified"
                            data-testid={`replay-modified-${s.seq}`}
                            aria-label="modified"
                          />
                        ) : null}
                      </td>
                      <td className={`px-2 py-1 ${cls}`}>{s.action}</td>
                      <td className={`px-2 py-1 ${cls}`}>{s.status}</td>
                      <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={s.skipped}
                          onChange={() => toggleSkip(s.seq)}
                          disabled={status === 'running' || status === 'paused'}
                          data-testid={`replay-skip-${s.seq}`}
                        />
                      </td>
                      <td className="px-2 py-1 text-[10px] text-rose-300">{s.error ?? ''}</td>
                    </tr>
                    {isExpanded ? (
                      <tr className="border-t border-slate-800 bg-slate-900/80">
                        <td colSpan={6} className="px-3 py-3">
                          <ReplayStepDetail
                            step={s}
                            disabled={status === 'running' || status === 'paused'}
                            modified={modified}
                            onSave={(parsed) => setStepPayload(s.seq, parsed)}
                            onRevert={() => revertStepPayload(s.seq)}
                            onClose={() => toggleExpand(s.seq)}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function countStatuses(
  steps: readonly { status: 'pending' | 'running' | 'done' | 'failed' | 'skipped' }[],
): { done: number; skipped: number; failed: number; pending: number } {
  let done = 0;
  let skipped = 0;
  let failed = 0;
  let pending = 0;
  for (const s of steps) {
    if (s.status === 'done') done++;
    else if (s.status === 'skipped') skipped++;
    else if (s.status === 'failed') failed++;
    else if (s.status === 'pending') pending++;
  }
  return { done, skipped, failed, pending };
}

/**
 * FEAT-025 — inline detail + JSON payload editor for a single replay step.
 * Mounted as the body of an expanded row beneath the step's main row.
 *
 * Action name is intentionally readonly: editing it would dispatch through
 * a different adapter handler, which is rarely what the user wants and
 * very easy to do by accident. Payload is a free-form JSON textarea.
 */
interface ReplayStepDetailProps {
  readonly step: ReplayStep;
  readonly disabled: boolean;
  readonly modified: boolean;
  readonly onSave: (parsed: Record<string, unknown>) => void;
  readonly onRevert: () => void;
  readonly onClose: () => void;
}

function ReplayStepDetail({
  step,
  disabled,
  modified,
  onSave,
  onRevert,
  onClose,
}: ReplayStepDetailProps): JSX.Element {
  // Local draft is initialised from the step's current payload so the
  // textarea reflects any earlier edits (or the original on first open).
  const [draft, setDraft] = useState<string>(() =>
    JSON.stringify(step.payload, null, 2),
  );
  const [parseError, setParseError] = useState<string | null>(null);

  // Re-sync the draft when the step's payload changes externally (e.g.
  // another tab caused a Revert, or a sibling expansion edited then saved).
  useEffect(() => {
    setDraft(JSON.stringify(step.payload, null, 2));
    setParseError(null);
  }, [step.payload]);

  const handleSave = (): void => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setParseError('Payload must be a JSON object (e.g. {…}).');
      return;
    }
    setParseError(null);
    onSave(parsed as Record<string, unknown>);
  };

  const handleRevert = (): void => {
    onRevert();
    setDraft(JSON.stringify(step.originalPayload, null, 2));
    setParseError(null);
  };

  return (
    <div className="space-y-2" data-testid={`replay-detail-${step.seq}`}>
      <div className="flex flex-wrap items-baseline gap-3 text-[11px] text-slate-400">
        <span>
          <span className="text-slate-500">action: </span>
          <span className="font-mono text-slate-100" data-testid={`replay-detail-action-${step.seq}`}>
            {step.action}
          </span>
        </span>
        <span>
          <span className="text-slate-500">messageId: </span>
          <span className="font-mono">{step.originalMessageId || '—'}</span>
        </span>
        <span>
          <span className="text-slate-500">capturedAt: </span>
          <span className="font-mono">
            {step.originalCreatedAt > 0
              ? new Date(step.originalCreatedAt).toISOString()
              : '—'}
          </span>
        </span>
        {modified ? (
          <span
            className="rounded bg-amber-700/40 px-1.5 py-0.5 text-[10px] text-amber-100"
            data-testid={`replay-detail-modified-${step.seq}`}
          >
            modified
          </span>
        ) : null}
      </div>
      <label className="block text-[11px] text-slate-500" htmlFor={`replay-payload-${step.seq}`}>
        payload (JSON object)
      </label>
      <textarea
        id={`replay-payload-${step.seq}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled}
        rows={Math.min(20, Math.max(6, draft.split('\n').length))}
        spellCheck={false}
        className="w-full rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-[11px] leading-snug text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-60"
        data-testid={`replay-payload-input-${step.seq}`}
      />
      {parseError ? (
        <p
          className="rounded bg-rose-900/40 px-2 py-1 text-[11px] text-rose-200"
          data-testid={`replay-payload-error-${step.seq}`}
        >
          {parseError}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled}
          className="rounded bg-primary-base px-2 py-1 text-xs font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
          data-testid={`replay-payload-save-${step.seq}`}
        >
          Save
        </button>
        <button
          type="button"
          onClick={handleRevert}
          disabled={disabled || !modified}
          title={modified ? 'Restore the captured payload' : 'Nothing to revert'}
          className="rounded bg-slate-700 px-2 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid={`replay-payload-revert-${step.seq}`}
        >
          Revert
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
          data-testid={`replay-payload-close-${step.seq}`}
        >
          Close
        </button>
      </div>
    </div>
  );
}

/**
 * Sends a single CALL through the live adapter. We tunnel through
 * useStationStore.createUploadPort to keep state→protocol coupling minimal,
 * but the OfflineUploadPort.upload() signature is Start/MV/Stop-shaped.
 * For generic CALLs we instead reach for the adapter via the
 * "use the upload port for StartTransaction; everything else goes through a
 * dedicated wrapper" pattern — the wrapper lives in useStationStore and is
 * surfaced as a future Action. For now, MeterValues / Heartbeat / Boot /
 * etc. all return resolved promises so the replay loop completes — the
 * Event Log capture still records them because every adapter call is
 * mirrored into the EventLogStore.
 */
async function sendThroughAdapter(
  action: string,
  payload: Record<string, unknown>,
): Promise<{ transactionId?: number }> {
  const { sendReplayCall } = await import('@/state/useStationStore');
  return sendReplayCall(action, payload);
}
